import React from 'react';
import { Card, Table, Tag, Statistic, Row, Col, Empty, Skeleton, Button, Dropdown } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { useCurrency } from '../../../hooks/useCurrency';
import { useGetDiscrepancySummaryQuery } from '../../../services/reportsApi';
import { useAppSelector } from '../../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../../utils/reportExport';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import { formatDateOnly } from '../../../utils/dateHelpers';

interface DiscrepancyAnalysisProps {
  restaurantId: string;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  customDateRange?: [any, any] | null;
}

const DiscrepancyAnalysis: React.FC<DiscrepancyAnalysisProps> = ({
  restaurantId,
  fromDate,
  toDate,
  selectedPeriod,
  customDateRange,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data, isLoading } = useGetDiscrepancySummaryQuery(
    { restaurantId, fromDate, toDate },
    { skip: !restaurantId || !fromDate || !toDate }
  );

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (!data || data.totalShiftsReviewed === 0) {
    return (
      <Card>
        <Empty description="No hay datos de discrepancia en este periodo" />
      </Card>
    );
  }

  const getVarianceColor = (value: number): string => {
    if (value < 0) return 'var(--color-error-text)';
    if (value > 0) return 'var(--color-success-text)';
    return '#1890ff';
  };

  const getTagColor = (value: number): string => {
    if (value < 0) return 'red';
    if (value > 0) return 'green';
    return 'blue';
  };

  const formatMoney = (value: number): string => {
    return `${currencySymbol} ${Math.abs(value).toFixed(2)}`;
  };

  const discrepancyPercentage =
    data.totalShiftsReviewed > 0
      ? ((data.shiftsWithDiscrepancy / data.totalShiftsReviewed) * 100).toFixed(1)
      : '0.0';

  const columns = [
    {
      title: 'Cajero',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Turnos',
      dataIndex: 'shiftsCount',
      key: 'shifts',
    },
    {
      title: 'Varianza Total',
      dataIndex: 'totalVariance',
      key: 'totalVariance',
      sorter: (a: any, b: any) => a.totalVariance - b.totalVariance,
      render: (value: number) => (
        <Tag color={getTagColor(value)}>
          {value < 0 ? '-' : value > 0 ? '+' : ''}
          {formatMoney(value)}
        </Tag>
      ),
    },
    {
      title: 'Varianza Promedio',
      dataIndex: 'averageVariance',
      key: 'averageVariance',
      sorter: (a: any, b: any) => a.averageVariance - b.averageVariance,
      defaultSortOrder: 'ascend' as const,
      render: (value: number) => (
        <Tag color={getTagColor(value)}>
          {value < 0 ? '-' : value > 0 ? '+' : ''}
          {formatMoney(value)}
        </Tag>
      ),
    },
    {
      title: 'Peor Varianza',
      dataIndex: 'worstVariance',
      key: 'worstVariance',
      render: (value: number) => (
        <Tag color={getTagColor(value)}>
          {value < 0 ? '-' : value > 0 ? '+' : ''}
          {formatMoney(value)}
        </Tag>
      ),
    },
    {
      title: 'Faltantes',
      dataIndex: 'shortageCount',
      key: 'shortages',
      render: (value: number) => <Tag color="red">{value}</Tag>,
    },
    {
      title: 'Sobrantes',
      dataIndex: 'overageCount',
      key: 'overages',
      render: (value: number) => <Tag color="green">{value}</Tag>,
    },
    {
      title: 'Perfectos',
      dataIndex: 'perfectCount',
      key: 'perfect',
      render: (value: number) => <Tag color="blue">{value}</Tag>,
    },
  ];

  const handleExportGrid = (fmt: 'csv' | 'excel' | 'pdf') => {
    const periodLabel = getPeriodLabel(selectedPeriod || 'today', customDateRange);
    const sections: ExportConfig['sections'] = [
      {
        title: 'Discrepancia por Cajero',
        titleStyle: 'cashier',
        headers: ['Cajero', 'Turnos', 'Varianza Total', 'Varianza Promedio', 'Peor Varianza', 'Faltantes', 'Sobrantes', 'Perfectos'],
        rows: (data.byUser ? [...data.byUser].sort((a: any, b: any) => a.averageVariance - b.averageVariance) : []).map((u: any) => [
          u.userName,
          u.shiftsCount,
          `${u.totalVariance < 0 ? '-' : u.totalVariance > 0 ? '+' : ''}${formatMoney(u.totalVariance)}`,
          `${u.averageVariance < 0 ? '-' : u.averageVariance > 0 ? '+' : ''}${formatMoney(u.averageVariance)}`,
          `${u.worstVariance < 0 ? '-' : u.worstVariance > 0 ? '+' : ''}${formatMoney(u.worstVariance)}`,
          u.shortageCount,
          u.overageCount,
          u.perfectCount,
        ]),
      },
    ];

    if (data.trends && data.trends.length > 0) {
      sections.push({
        title: 'Tendencia de Varianza',
        titleStyle: 'section',
        headers: ['Fecha', 'Faltante', 'Sobrante', 'Varianza Neta'],
        rows: data.trends.map((t: any) => {
          const netVariance = (t.overageAmount || 0) - Math.abs(t.shortageAmount || 0);
          return [
            formatDateOnly(t.date, 'DD/MM/YYYY'),
            `${currencySymbol} ${Math.abs(t.shortageAmount || 0).toFixed(2)}`,
            `${currencySymbol} ${(t.overageAmount || 0).toFixed(2)}`,
            `${netVariance < 0 ? '-' : netVariance > 0 ? '+' : ''}${currencySymbol} ${Math.abs(netVariance).toFixed(2)}`,
          ];
        }),
      });
    }

    const config: ExportConfig = {
      reportTitle: 'Analisis de Discrepancias',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Total Faltantes', value: `- ${currencySymbol} ${Math.abs(data.totalShortages).toFixed(2)}` },
        { label: 'Total Sobrantes', value: `+ ${currencySymbol} ${data.totalOverages.toFixed(2)}` },
        { label: 'Varianza Promedio', value: `${data.averageVariance < 0 ? '-' : data.averageVariance > 0 ? '+' : ''} ${currencySymbol} ${Math.abs(data.averageVariance).toFixed(2)}` },
        { label: 'Turnos con Diferencia', value: `${discrepancyPercentage}% (${data.shiftsWithDiscrepancy} / ${data.totalShiftsReviewed})` },
      ],
      sections,
    };

    if (fmt === 'csv') exportCSV(config, 'discrepancias');
    else if (fmt === 'excel') exportExcel(config, 'discrepancias');
    else exportPDF(config);
  };

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExportGrid('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExportGrid('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExportGrid('pdf') },
  ];

  const maxBarValue =
    data.trends && data.trends.length > 0
      ? Math.max(
          ...data.trends.map((t: any) =>
            Math.max(Math.abs(t.shortageAmount || 0), Math.abs(t.overageAmount || 0))
          ),
          1
        )
      : 1;

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Faltantes"
              value={Math.abs(data.totalShortages)}
              precision={2}
              prefix={`- ${currencySymbol}`}
              valueStyle={{ color: 'var(--color-error-text)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Sobrantes"
              value={data.totalOverages}
              precision={2}
              prefix={`+ ${currencySymbol}`}
              valueStyle={{ color: 'var(--color-success-text)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Varianza Promedio"
              value={Math.abs(data.averageVariance)}
              precision={2}
              prefix={`${data.averageVariance < 0 ? '-' : data.averageVariance > 0 ? '+' : ''} ${currencySymbol}`}
              valueStyle={{ color: getVarianceColor(data.averageVariance) }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Turnos con Diferencia"
              value={Number(discrepancyPercentage)}
              precision={1}
              suffix={`% (${data.shiftsWithDiscrepancy} / ${data.totalShiftsReviewed})`}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Dropdown menu={{ items: exportMenuItems }}>
            <Button icon={<DownloadOutlined />} size="small">
              Exportar este grid
            </Button>
          </Dropdown>
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={
              data.byUser
                ? [...data.byUser].sort((a: any, b: any) => a.averageVariance - b.averageVariance)
                : []
            }
            rowKey={(_, index) => String(index)}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={isMobile ? { x: 900 } : undefined}
          />
        </div>
      </Card>

      <Card title="Tendencia de Varianza" style={{ marginTop: 16 }}>
        {!data.trends || data.trends.length === 0 ? (
          <Empty description="Sin datos de tendencia" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.trends.map((trend: any, index: number) => {
              const shortageWidth =
                maxBarValue > 0
                  ? (Math.abs(trend.shortageAmount || 0) / maxBarValue) * 100
                  : 0;
              const overageWidth =
                maxBarValue > 0
                  ? (Math.abs(trend.overageAmount || 0) / maxBarValue) * 100
                  : 0;
              const netVariance = (trend.overageAmount || 0) - Math.abs(trend.shortageAmount || 0);

              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      width: 80,
                      flexShrink: 0,
                      textAlign: 'right',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {formatDateOnly(trend.date, 'DD/MM/YYYY')}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                    }}
                  >
                    {/* Shortage (red, left side) */}
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <div
                        style={{
                          width: `${shortageWidth}%`,
                          maxWidth: '100%',
                          height: 20,
                          backgroundColor: '#ff4d4f',
                          borderRadius: '4px 0 0 4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    {/* Center divider */}
                    <div
                      style={{
                        width: 2,
                        height: 24,
                        backgroundColor: 'var(--border-color-strong)',
                        flexShrink: 0,
                      }}
                    />
                    {/* Overage (green, right side) */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          width: `${overageWidth}%`,
                          maxWidth: '100%',
                          height: 20,
                          backgroundColor: '#52c41a',
                          borderRadius: '0 4px 4px 0',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>
                  <span
                    style={{
                      width: 120,
                      flexShrink: 0,
                      textAlign: 'left',
                      color: getVarianceColor(netVariance),
                      fontWeight: 500,
                    }}
                  >
                    {netVariance < 0 ? '-' : netVariance > 0 ? '+' : ''}
                    {currencySymbol} {Math.abs(netVariance).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default DiscrepancyAnalysis;
